import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { SocialMediaPlannerService } from '../../services/socialMediaPlannerService';
import { Campaign } from '../../types/socialMediaPlanner';

interface CampaignFormProps {
  campaign?: Campaign;
  onSave?: (campaign: Campaign) => void;
  onCancel?: () => void;
  className?: string;
}

/**
 * Form for creating or editing a social media campaign
 */
const CampaignForm: React.FC<CampaignFormProps> = ({ 
  campaign, 
  onSave,
  onCancel,
  className = '' 
}) => {
  // Initialize with empty campaign or the provided one for editing
  const [formData, setFormData] = useState<Partial<Campaign>>({
    name: '',
    description: '',
    status: 'draft',
    tags: [],
    goal: 'increase brand awareness', // Default goal as string
    metrics: {
      impressions: 0,
      engagement: 0,
      clicks: 0,
      conversions: 0,
      custom: {}, // Custom metrics as key-value pairs
    },
    posts: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const plannerService = new SocialMediaPlannerService();

  // If campaign is provided, use it to initialize the form (edit mode)
  useEffect(() => {
    if (campaign) {
      setFormData({
        ...campaign,
        // Ensure object fields are properly initialized even if not in the original campaign
        goal: campaign.goal || 'increase brand awareness',
        metrics: campaign.metrics || {
          impressions: 0,
          engagement: 0,
          clicks: 0,
          conversions: 0,
          custom: {}, // Custom metrics as key-value pairs
        },
        tags: campaign.tags || [],
        posts: campaign.posts || [],
      });
    }
  }, [campaign]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle nested fields with dot notation (e.g., "goals.engagement")
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...(formData[parent as keyof typeof formData] as Record<string, any> || {}),
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseInt(value, 10);
    
    // Handle nested fields with dot notation
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...(formData[parent as keyof typeof formData] as Record<string, any> || {}),
          [child]: isNaN(numValue) ? 0 : numValue,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: isNaN(numValue) ? 0 : numValue,
      });
    }
  };

  const handleAddTag = () => {
    if (!tagInput.trim()) return;
    
    const newTag = tagInput.trim();
    const currentTags = formData.tags || [];
    
    if (!currentTags.includes(newTag)) {
      setFormData({
        ...formData,
        tags: [...currentTags, newTag],
      });
    }
    
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: (formData.tags || []).filter(tag => tag !== tagToRemove),
    });
  };

  const validateForm = (): boolean => {
    if (!formData.name?.trim()) {
      toast.error('Campaign name is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setIsSubmitting(true);
      
      let savedCampaign;
      
      if (campaign?.id) {
        // Update existing campaign
        savedCampaign = await plannerService.updateCampaign({
          ...formData,
          id: campaign.id,
        } as Campaign);
        toast.success('Campaign updated successfully');
      } else {
        // Create new campaign
        savedCampaign = await plannerService.createCampaign(formData as Campaign);
        toast.success('Campaign created successfully');
      }
      
      if (onSave) {
        onSave(savedCampaign);
      }
    } catch (err: any) {
      console.error('Error saving campaign:', err);
      toast.error(err?.userMessage || 'Failed to save campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      <h2 className="text-xl font-semibold text-gray-800 mb-6">
        {campaign ? 'Edit Campaign' : 'Create New Campaign'}
      </h2>
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Campaign Name */}
          <div className="col-span-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Campaign Name*
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name || ''}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          
          {/* Campaign Description */}
          <div className="col-span-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={3}
              value={formData.description || ''}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Campaign Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status || 'draft'}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          
          {/* Campaign Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tags
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                className="flex-1 min-w-0 block w-full border border-gray-300 rounded-l-md py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add a tag"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 hover:bg-gray-100"
              >
                Add
              </button>
            </div>
            {(formData.tags || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {(formData.tags || []).map((tag, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 flex-shrink-0 text-blue-400 hover:text-blue-500 focus:outline-none"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          
          <div className="col-span-2 mt-4">
            <h3 className="text-lg font-medium text-gray-900">Campaign Goal</h3>
            <p className="mt-1 text-sm text-gray-500">Define the main goal for this campaign</p>
            <div className="mt-3">
              <label htmlFor="goal" className="block text-sm font-medium text-gray-700">
                Campaign Goal
              </label>
              <input
                type="text"
                id="goal"
                name="goal"
                value={formData.goal || ''}
                onChange={(e) => setFormData({...formData, goal: e.target.value})}
                placeholder="E.g., Increase brand awareness, drive website traffic, generate leads"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
        
        <div className="mt-8 flex justify-end">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Saving...' : 'Save Campaign'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CampaignForm;
